import { WarningFilled } from '@ant-design/icons';
import { Flex } from 'antd';
import React from 'react';
import ReactDOM from 'react-dom/client';

import ConfirmDialog from '~/common/components/dialogs/ConfirmDialog';
import MessageDialog from '~/common/components/dialogs/MessageDialog';
import Body from '~/common/components/typography/Body';
import { COLORS } from '~/configs/theme';

import type { Root } from 'react-dom/client';

import type { ConfirmProps } from '~/common/components/dialogs/ConfirmDialog';
import type { MessageProps } from '~/common/components/dialogs/MessageDialog';

const DIALOG_DOM_ID = '__dialog__';

const message = ({
  title,
  content,
  confirmLabel,
  maskClosable,
}: Omit<MessageProps, 'children'> & { content: React.ReactNode }): Promise<void> => {
  const place = document.getElementById(DIALOG_DOM_ID);

  let dialogResolver: (() => void) | null = null;

  const open = () => {
    if (place) {
      const root = ReactDOM.createRoot(place);
      root.render(
        <MessageDialog
          title={title}
          confirmLabel={confirmLabel}
          maskClosable={maskClosable}
          onConfirm={() => handleClose(root)}
        >
          <Body>{content}</Body>
        </MessageDialog>,
      );
    }

    return new Promise<void>(resolve => {
      dialogResolver = resolve;
    });
  };

  const handleClose = (root: Root) => {
    root.unmount();

    if (dialogResolver) {
      dialogResolver();
    }
  };

  return open();
};

const confirm = ({
  title,
  content,
  confirmLabel,
  cancelLabel,
  okDanger,
}: Omit<ConfirmProps, 'children'> & { content: React.ReactNode }) => {
  const place = document.getElementById(DIALOG_DOM_ID);

  let confirmResolver: ((value: boolean) => void) | null = null;

  const open = () => {
    if (place) {
      const root = ReactDOM.createRoot(place);
      root.render(
        <ConfirmDialog
          title={title}
          cancelLabel={cancelLabel}
          confirmLabel={confirmLabel}
          okDanger={okDanger}
          onCancel={() => handleCancel(root)}
          onConfirm={() => handleConfirm(root)}
        >
          <Body>{content}</Body>
        </ConfirmDialog>,
      );
    }

    return new Promise<boolean>(resolve => {
      confirmResolver = resolve;
    });
  };

  const handleClose = (root: Root) => {
    root.unmount();
  };

  const handleConfirm = (root: Root) => {
    if (confirmResolver) {
      confirmResolver(true);
    }
    handleClose(root);
  };

  const handleCancel = (root: Root) => {
    if (confirmResolver) {
      confirmResolver(false);
    }
    handleClose(root);
  };

  return open();
};

/**
 * confirm 의 경고 변형 — title 앞에 ⚠ 아이콘을 자동으로 붙인다.
 * 매장 일괄 변경·관리자 PC 배정처럼 비가역/주의가 필요한 액션에 사용.
 */
const warn = ({ title, ...rest }: Omit<ConfirmProps, 'children'> & { content: React.ReactNode }) =>
  confirm({
    ...rest,
    title: (
      <Flex align="center" gap={8}>
        <WarningFilled style={{ color: COLORS.YELLOW['400'], fontSize: 20 }} />
        {title}
      </Flex>
    ),
  });

const dialog = { confirm, message, warn };

export default dialog;
